import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AppColors } from '../theme/colors';
import { signUpWithEmail } from '../services/firebase/auth';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password !== confirmPassword) {
      setError('Паролі не співпадають');
      return;
    }

    if (password.length < 6) {
      setError('Пароль має бути не менше 6 символів');
      return;
    }

    if (name.trim().length < 2) {
      setError('Будь ласка, введіть повне ім\'я');
      return;
    }

    setLoading(true);

    try {
      await signUpWithEmail(email, password, name.trim(), 'student');

      // Check for saved return URL (e.g., from join-class link)
      const returnUrl = sessionStorage.getItem('authReturnUrl');
      sessionStorage.removeItem('authReturnUrl');

      if (returnUrl) {
        // Return to the original URL (preserves query params like ?code=)
        navigate(returnUrl);
      } else {
        navigate('/join-class');
      }
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message || 'Не вдалося створити акаунт');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: AppColors.bgPrimary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflowY: 'auto',
        padding: 'clamp(16px, 4vw, 24px)',
        paddingTop: 'clamp(40px, 10vh, 80px)',
        paddingBottom: 'clamp(40px, 10vh, 80px)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          background: AppColors.surfaceDark,
          borderRadius: 'clamp(16px, 4vw, 24px)',
          padding: 'clamp(24px, 6vw, 40px)',
          border: `1px solid ${AppColors.borderColor}`,
        }}
      >
        {/* Logo/Header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(20px, 5vw, 28px)' }}>
          <div
            style={{
              fontSize: 'clamp(40px, 10vw, 56px)',
              marginBottom: 'clamp(8px, 2vw, 12px)',
            }}
          >
            🎓
          </div>
          <h1
            style={{
              fontSize: 'clamp(24px, 6vw, 32px)',
              fontWeight: 700,
              color: AppColors.textPrimary,
              margin: 0,
            }}
          >
            Створити акаунт
          </h1>
          <p
            style={{
              fontSize: 'clamp(14px, 3.5vw, 16px)',
              color: AppColors.textSecondary,
              margin: 'clamp(8px, 2vw, 12px) 0 0 0',
            }}
          >
            Почніть вивчати англійську
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: 'clamp(10px, 2.5vw, 14px)',
              background: 'rgba(248, 113, 113, 0.15)',
              border: `1px solid ${AppColors.errorRose}`,
              borderRadius: 'clamp(8px, 2vw, 12px)',
              marginBottom: 'clamp(16px, 4vw, 20px)',
              color: AppColors.errorRose,
              fontSize: 'clamp(13px, 3vw, 14px)',
            }}
          >
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Name Field */}
          <div style={{ marginBottom: 'clamp(14px, 3.5vw, 18px)' }}>
            <label
              style={{
                display: 'block',
                fontSize: 'clamp(13px, 3vw, 14px)',
                fontWeight: 500,
                color: AppColors.textSecondary,
                marginBottom: 'clamp(6px, 1.5vw, 8px)',
              }}
            >
              Повне ім'я
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Введіть ваше ім'я"
              required
              style={{
                width: '100%',
                height: 'clamp(44px, 10vw, 52px)',
                background: AppColors.surfaceLight,
                border: `1px solid ${AppColors.borderColor}`,
                borderRadius: 'clamp(10px, 2.5vw, 12px)',
                padding: '0 clamp(14px, 3.5vw, 18px)',
                color: AppColors.textPrimary,
                fontSize: 'clamp(15px, 3.5vw, 16px)',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>

          {/* Email Field */}
          <div style={{ marginBottom: 'clamp(14px, 3.5vw, 18px)' }}>
            <label
              style={{
                display: 'block',
                fontSize: 'clamp(13px, 3vw, 14px)',
                fontWeight: 500,
                color: AppColors.textSecondary,
                marginBottom: 'clamp(6px, 1.5vw, 8px)',
              }}
            >
              Електронна пошта
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Введіть вашу електронну пошту"
              required
              style={{
                width: '100%',
                height: 'clamp(44px, 10vw, 52px)',
                background: AppColors.surfaceLight,
                border: `1px solid ${AppColors.borderColor}`,
                borderRadius: 'clamp(10px, 2.5vw, 12px)',
                padding: '0 clamp(14px, 3.5vw, 18px)',
                color: AppColors.textPrimary,
                fontSize: 'clamp(15px, 3.5vw, 16px)',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>

          {/* Password Field */}
          <div style={{ marginBottom: 'clamp(14px, 3.5vw, 18px)' }}>
            <label
              style={{
                display: 'block',
                fontSize: 'clamp(13px, 3vw, 14px)',
                fontWeight: 500,
                color: AppColors.textSecondary,
                marginBottom: 'clamp(6px, 1.5vw, 8px)',
              }}
            >
              Пароль
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Створіть пароль"
              required
              style={{
                width: '100%',
                height: 'clamp(44px, 10vw, 52px)',
                background: AppColors.surfaceLight,
                border: `1px solid ${AppColors.borderColor}`,
                borderRadius: 'clamp(10px, 2.5vw, 12px)',
                padding: '0 clamp(14px, 3.5vw, 18px)',
                color: AppColors.textPrimary,
                fontSize: 'clamp(15px, 3.5vw, 16px)',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>

          {/* Confirm Password Field */}
          <div style={{ marginBottom: 'clamp(20px, 5vw, 28px)' }}>
            <label
              style={{
                display: 'block',
                fontSize: 'clamp(13px, 3vw, 14px)',
                fontWeight: 500,
                color: AppColors.textSecondary,
                marginBottom: 'clamp(6px, 1.5vw, 8px)',
              }}
            >
              Підтвердіть пароль
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Підтвердіть ваш пароль"
              required
              style={{
                width: '100%',
                height: 'clamp(44px, 10vw, 52px)',
                background: AppColors.surfaceLight,
                border: `1px solid ${AppColors.borderColor}`,
                borderRadius: 'clamp(10px, 2.5vw, 12px)',
                padding: '0 clamp(14px, 3.5vw, 18px)',
                color: AppColors.textPrimary,
                fontSize: 'clamp(15px, 3.5vw, 16px)',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              height: 'clamp(48px, 12vw, 56px)',
              background: loading
                ? AppColors.surfaceMedium
                : `linear-gradient(135deg, ${AppColors.accentPurple} 0%, ${AppColors.accentBlue} 100%)`,
              border: 'none',
              borderRadius: 'clamp(10px, 2.5vw, 12px)',
              color: loading ? AppColors.textSecondary : AppColors.textDark,
              fontSize: 'clamp(15px, 3.5vw, 17px)',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Створення акаунту...' : 'Створити акаунт'}
          </button>
        </form>

        {/* Sign In Link */}
        <div
          style={{
            textAlign: 'center',
            marginTop: 'clamp(20px, 5vw, 28px)',
            fontSize: 'clamp(14px, 3.5vw, 15px)',
            color: AppColors.textSecondary,
          }}
        >
          Вже є акаунт?{' '}
          <Link
            to="/login"
            style={{
              color: AppColors.accentPurple,
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            Увійти
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
